import React from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import LocationOn from '@mui/icons-material/LocationOn';
import Rating from '@mui/material/Rating';
import Chip from '@mui/material/Chip';
import PoolIcon from '@mui/icons-material/Pool';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import WifiIcon from '@mui/icons-material/Wifi';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import BedIcon from '@mui/icons-material/Bed';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { roomServiceType } from '../../types';
import { HotelDetail } from 'features/hotels/types';
import { Box, Button, CardActions, Grid, Radio } from '@mui/material';
import { getBoardName, GetPAXTotalsResponse, openInNewTabHotelUrl, separadorDeMiles } from '../../helpers';
import { useAgencyBranding } from '../../branding';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		title: {
			color: theme.palette.primary.main,
			fontWeight: 'bold',
			fontSize: '0.98rem',
			lineHeight: 1.15,
		},
		address: {
			fontSize: '0.75rem',
			lineHeight: 1.25,
		},
		media: {
			position: 'relative',
			height: 0,
			paddingTop: '58%',
			backgroundColor: '#f2eef6',
			overflow: 'hidden',
		},
		mediaImg: {
			position: 'absolute',
			top: 0,
			left: 0,
			width: '100%',
			height: '100%',
			objectFit: 'cover' as const,
			display: 'block',
		},
		opinions: {
			fontSize: '11px',
			fontStyle: 'italic',
			fontWeight: 400,
			letterSpacing: '0em',
		},
		facilitiesIcon: {
			fontWeight: 'bold',
			fontSize: '20px',
		},
		flex: {
			display: 'flex',
		},
		ratingMolecule: {
			display: 'flex',
			alignItems: 'center',
			paddingTop: theme.spacing(0.75),
		},
		evaluation: {
			color: '#71BCED',
			fontSize: '0.75rem',
		},
		ratingText: {
			paddingLeft: theme.spacing(2),
		},
		powered: {
			paddingLeft: theme.spacing(2),
			width: '115px',
		},
		hotelStars: {
			display: 'flex',
			paddingTop: theme.spacing(1),
		},
		webSiteContainer: {
			display: 'flex',
			justifyContent: 'flex-end',
		},
	}),
);
/**
 * Lo mínimo que la card necesita de una opción de hotel. Es un subconjunto
 * estructural tanto de HotelOptionDetailDestinationMultiple (multi-destino)
 * como de HotelOptionsSimple (un destino), así la misma card sirve para los
 * dos tipos de cotización.
 */
export interface HotelCardDisplayRoom {
	board: roomServiceType;
	description?: string;
}

export interface HotelCardDisplayOption {
	hotelId: number;
	details: HotelDetail | undefined;
	rooms: HotelCardDisplayRoom[];
}

interface Props {
	hotelOption: HotelCardDisplayOption;
	isSelected: boolean;
	handleSelectHotel: () => void;
	isExternal?: boolean;
	paxTotals?: GetPAXTotalsResponse;
}

export const HotelDetailCardMultiDestination = ({
	hotelOption,
	isSelected,
	handleSelectHotel,
	isExternal,
	paxTotals,
}: Props): JSX.Element => {
	const classes = useStyles();
	const branding = useAgencyBranding(isExternal);
	const boardNames = Array.from(new Set(hotelOption.rooms.map((room) => getBoardName(room.board)).filter(Boolean)));
	const getRoomPaxText = (index: number) => {
		const pax = paxTotals?.totalPaxPerRoom[index];
		if (!pax) return null;
		const parts = [`${pax.totalAdults} ${pax.totalAdults === 1 ? 'adulto' : 'adultos'}`];
		if (pax.hasChild) parts.push(`${pax.totalChild} ${pax.totalChild === 1 ? 'niño' : 'niños'}`);
		if (pax.hasInfant) parts.push(`${pax.totalInfant} ${pax.totalInfant === 1 ? 'bebé' : 'bebés'}`);
		return parts.join(' + ');
	};
	const visibleAmenities = [
		{
			label: 'Piscina',
			icon: <PoolIcon color="secondary" className={classes.facilitiesIcon} />,
			show: hotelOption.details?.pool,
		},
		{
			label: 'Wifi',
			icon: <WifiIcon color="secondary" className={classes.facilitiesIcon} />,
			show: hotelOption.details?.wifi,
		},
		{
			label: 'A/C',
			icon: <AcUnitIcon color="secondary" className={classes.facilitiesIcon} />,
			show: hotelOption.details?.airConditioning,
		},
		{
			label: 'Parking',
			icon: <LocalParkingIcon color="secondary" className={classes.facilitiesIcon} />,
			show: hotelOption.details?.parking,
		},
		{
			label: 'Gym',
			icon: <FitnessCenterIcon color="secondary" className={classes.facilitiesIcon} />,
			show: hotelOption.details?.gym,
		},
	].filter((amenity) => amenity.show);

	return (
		<Card
			sx={{
				display: 'flex',
				justifyContent: 'space-between',
				flexDirection: 'column',
				position: 'relative',
				border: isSelected ? '2px solid' : '1px solid',
				borderColor: isSelected ? branding.hotelCardSelectedBg : 'rgba(49, 25, 75, 0.12)',
				boxShadow: isSelected ? '0 14px 34px rgba(49, 25, 75, 0.22)' : '0 5px 16px rgba(49, 25, 75, 0.08)',
				overflow: 'hidden',
				borderRadius: '10px',
				transition: 'box-shadow 160ms ease, transform 160ms ease, border-color 160ms ease',
				transform: isSelected ? 'translateY(-2px)' : 'none',
				backgroundColor: '#fff',
				height: '100%',
			}}
		>
			<Box
				onClick={handleSelectHotel}
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					width: '100%',
					position: 'absolute',
					zIndex: 2,
					backgroundColor: isSelected ? branding.hotelCardSelectedBg : branding.hotelCardHeaderBg,
					color: 'white',
					px: 1,
					py: 0.65,
					cursor: 'pointer',
					backdropFilter: 'blur(6px)',
				}}
			>
				<Box display="flex" alignItems="center" gap={0.75}>
					{isSelected && <CheckCircleIcon sx={{ fontSize: 16, color: 'secondary.main' }} />}
					<Typography variant="caption" fontWeight="bold">
						{isSelected ? 'Hotel seleccionado' : 'Tocar para seleccionar'}
					</Typography>
				</Box>
				<Radio
					sx={{
						color: 'white',
						p: 0,
						'&.Mui-checked': {
							color: 'secondary.main',
						},
					}}
					checked={isSelected}
					onChange={handleSelectHotel}
				/>
			</Box>
			{/* Sin foto no reservamos espacio: la card arranca en el nombre del hotel.
			    Con foto usamos <img loading="lazy"> en vez de background-image: una
			    cotización puede traer 40 hoteles y el navegador sólo baja los que
			    están por entrar en pantalla (clave en Android de gama media con
			    datos móviles). El wrapper mantiene la proporción 58% de antes. */}
			{hotelOption.details?.mainImage && (
				<Box className={classes.media}>
					<img
						src={hotelOption.details.mainImage}
						alt={hotelOption.details?.name || 'Hotel'}
						title={hotelOption.details?.name}
						loading="lazy"
						decoding="async"
						className={classes.mediaImg}
					/>
				</Box>
			)}
			<CardContent sx={{ pt: 1.5, pb: 1 }}>
				<Grid container spacing={0.75}>
					<Grid item xs={12}>
						{/* Sin noWrap: el nombre del hotel nunca se trunca. */}
						<Typography component="h5" variant="h6" className={classes.title}>
							{hotelOption.details?.name}
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Box className={classes.flex} alignItems="flex-start" gap={0.5}>
							<LocationOn color="secondary" sx={{ fontSize: 17, mt: 0.15 }} />
							<Typography variant="subtitle1" color="textSecondary" className={classes.address}>
								{hotelOption.details?.address}
							</Typography>
						</Box>
					</Grid>
					<Grid item xs={12}>
						<Rating name="read-only" value={hotelOption.details?.accommodationCategory} readOnly size="small" />
					</Grid>
					{hotelOption.details?.rating != null && (
						<Grid item xs={12}>
							{/* Puntaje estilo Booking: el número solo no dice nada — va con su
							    texto y la cantidad de opiniones para darle escala y contexto. */}
							<Box display="flex" alignItems="center" gap={1}>
								<Box
									sx={{
										px: 0.9,
										py: 0.5,
										borderRadius: '8px 8px 8px 2px',
										backgroundColor: branding.accent,
										color: 'white',
										fontWeight: 800,
										fontSize: 13,
										lineHeight: 1,
										flex: '0 0 auto',
									}}
								>
									{hotelOption.details.rating.toFixed(1)}
								</Box>
								<Box minWidth={0}>
									{hotelOption.details.ratingText && (
										<Typography variant="body2" fontWeight={800} lineHeight={1.15}>
											{hotelOption.details.ratingText}
										</Typography>
									)}
									<Typography variant="caption" color="text.secondary" lineHeight={1.15} display="block">
										{hotelOption.details.amountReviews
											? `${separadorDeMiles(hotelOption.details.amountReviews)} opiniones · Google`
											: 'Google'}
									</Typography>
								</Box>
							</Box>
						</Grid>
					)}
					<Grid item xs={12}>
						<Box
							sx={{
								mt: 0.5,
								p: 1,
								borderRadius: '8px',
								backgroundColor: 'rgba(49, 25, 75, 0.035)',
								border: '1px solid rgba(49, 25, 75, 0.06)',
							}}
						>
							<Box display="flex" alignItems="center" gap={0.75} mb={0.45}>
								<BedIcon color="primary" sx={{ fontSize: 18 }} />
								<Typography variant="caption" color="primary" fontWeight={800}>
									{hotelOption.rooms.length} {hotelOption.rooms.length === 1 ? 'habitación' : 'habitaciones'}
								</Typography>
							</Box>
							<Box display="flex" flexDirection="column" gap={0.35}>
								{hotelOption.rooms.slice(0, 4).map((room, index) => (
									<Box
										key={`${hotelOption.hotelId}-room-summary-${index}`}
										display="grid"
										gridTemplateColumns="44px minmax(0, 1fr)"
										columnGap={0.75}
										alignItems="baseline"
									>
										<Typography variant="caption" color="secondary" fontWeight={800} lineHeight={1.2}>
											Hab. {index + 1}
										</Typography>
										{/* Sin noWrap: la composición de la habitación es info de verificación. */}
										<Typography variant="caption" color="text.secondary" lineHeight={1.2}>
											{getRoomPaxText(index)}
											{getRoomPaxText(index) && room.description ? ' · ' : ''}
											{room.description}
										</Typography>
									</Box>
								))}
							</Box>
							{boardNames.length > 0 && (
								<Box display="flex" alignItems="center" gap={0.75} mt={0.75}>
									<RestaurantIcon color="secondary" sx={{ fontSize: 16 }} />
									<Typography variant="caption" color="text.primary" fontWeight={700}>
										{boardNames.join(' · ')}
									</Typography>
								</Box>
							)}
						</Box>
					</Grid>

					{hotelOption.details?.link != null && hotelOption.details?.link != '' && (
						<Grid item xs={12} className={classes.webSiteContainer}>
							<Button
								variant="text"
								color="primary"
								size="small"
								endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
								style={{ fontSize: '12px', fontStyle: 'normal', textTransform: 'none', fontWeight: 700 }}
								sx={isExternal ? { color: '#06036c' } : undefined}
								onClick={() => openInNewTabHotelUrl(hotelOption.details?.link || '')}
							>
								Ver hotel
							</Button>
						</Grid>
					)}
				</Grid>
			</CardContent>
			<CardActions sx={{ px: 1.25, pt: 0, pb: 1.25, mt: 'auto' }}>
				<Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" width="100%">
					{visibleAmenities.slice(0, 4).map((amenity) => (
						<Chip
							key={amenity.label}
							icon={amenity.icon}
							label={amenity.label}
							size="small"
							variant="outlined"
							sx={{
								height: 26,
								borderRadius: '999px',
								borderColor: 'rgba(49, 25, 75, 0.12)',
								fontSize: 11,
								fontWeight: 700,
							}}
						/>
					))}
					{visibleAmenities.length > 4 && (
						<Chip
							label={`+${visibleAmenities.length - 4}`}
							size="small"
							sx={{ height: 26, borderRadius: '999px', fontSize: 11, fontWeight: 800 }}
						/>
					)}
				</Box>
			</CardActions>
		</Card>
	);
};
